<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { DEFAULT_LOCALE } from '@/i18n'
import {
  subscriptionHref,
  type ProductKey,
  type StepKey,
} from '@/router/products'
import { useSubscriptionState } from '@/composables/useSubscriptionState'
import { useSubscriptionFlow } from '@/composables/useSubscriptionFlow'

const props = defineProps<{
  productKey?: ProductKey
  productLabel: string
}>()

const { t } = useI18n()
const { locale, answerPathSlugs, answers, sequence } = useSubscriptionState()
// Health add-on names come from the backend bundle (translated via `bundles`).
const { crossSellAddons: healthCrossSellAddons } = useSubscriptionFlow()

/** Une fois le produit/formule choisi (sortie de l'arbre de décision), on replie
 *  le récap par défaut ; il reste dépliable manuellement. */
const hasChosenProduct = computed(() => Boolean(answers.value.plan))
const isOpen = ref(!hasChosenProduct.value)
watch(hasChosenProduct, (chosen) => {
  isOpen.value = !chosen
})

const homePath = computed(() => (locale.value === DEFAULT_LOCALE ? '/' : `/${locale.value}/`))

/** URL pour revenir juste avant l'étape stepIdx (= retirer toutes les réponses depuis stepIdx). */
function urlBeforeStep(stepIdx: number): string {
  if (!props.productKey) return homePath.value
  return subscriptionHref(props.productKey, answerPathSlugs.value.slice(0, stepIdx), locale.value)
}

function stepLabel(stepKey: StepKey): string {
  if (stepKey === 'forWhom') return t('subscription.start.forWhom')
  if (stepKey === 'isStudent') {
    return answers.value.forWhom === 'child'
      ? t('subscription.start.studentChild')
      : t('subscription.start.studentSelf')
  }
  if (stepKey === 'age') {
    return answers.value.forWhom === 'child'
      ? t('subscription.start.ageQuestion.child')
      : t('subscription.start.ageQuestion.self')
  }
  if (stepKey === 'internetProtection') return t('subscription.start.internetRecapLabel')
  if (stepKey === 'destination') return t('subscription.start.destinationRecapLabel')
  if (stepKey === 'franceStay') return t('subscription.start.franceStayRecapLabel')
  if (stepKey === 'residency') return t('subscription.start.residencyRecapLabel')
  if (stepKey === 'careFrequency') return t('subscription.start.careRecapLabel')
  if (stepKey === 'opticalExpenses') return t('subscription.start.opticalRecapLabel')
  if (stepKey === 'dentalNeeds') return t('subscription.start.dentalRecapLabel')
  if (stepKey === 'extraReimbursements') return t('subscription.start.extraRecapLabel')
  if (stepKey === 'plan') return t('subscription.results.recapLabel')
  if (stepKey === 'topUp') return t('subscription.topUp.recapLabel')
  if (stepKey === 'crossSell') return t('subscription.crossSell.recapLabel')
  return ''
}

function answerLabel(stepKey: StepKey, value: string): string {
  if (stepKey === 'forWhom') return t(`subscription.start.forWhomOptions.${value}`)
  if (stepKey === 'isStudent') return t(`subscription.start.yesNo.${value}`)
  if (stepKey === 'age') {
    const branch = answers.value.forWhom === 'child' ? 'child' : 'self'
    const params = {
      min: branch === 'child' ? 15 : 18,
      max: props.productKey === 'abroad' ? 34 : 30,
    }
    return t(`subscription.start.ageOptions.${branch}.${value}`, params)
  }
  if (stepKey === 'destination') return t(`subscription.start.destinationOptions.${value}`)
  if (stepKey === 'franceStay') return t(`subscription.start.franceStayOptions.${value}`)
  if (stepKey === 'residency') return t(`subscription.start.yesNo.${value}`)
  if (stepKey === 'careFrequency') return t(`subscription.start.careOptions.${value}`)
  if (stepKey === 'opticalExpenses') return t(`subscription.start.opticalOptions.${value}`)
  if (stepKey === 'dentalNeeds') {
    const branch = answers.value.forWhom === 'child' ? 'child' : 'self'
    return t(`subscription.start.dentalOptions.${branch}.${value}`)
  }
  if (stepKey === 'extraReimbursements') return t(`subscription.start.yesNo.${value}`)
  if (stepKey === 'internetProtection') return t(`subscription.start.yesNo.${value}`)
  if (stepKey === 'plan') {
    if (value === 'tonic') return t('subscription.topUp.name')
    if (props.productKey === 'civil') return t(`subscription.civilResults.products.${value}.name`)
    if (props.productKey === 'abroad') return t(`subscription.abroadResults.products.${value}.name`)
    if (props.productKey === 'arrivingFrance') return t(`subscription.franceResults.products.${value}.name`)
    if (props.productKey === 'regionalAura') return t(`subscription.regionalResults.products.${value}.name`)
    return t(`subscription.results.tiers.${value}`)
  }
  if (stepKey === 'topUp') {
    return value === 'subscribe'
      ? t('subscription.topUp.subscribed')
      : t('subscription.topUp.skipped')
  }
  if (stepKey === 'crossSell') {
    if (value === 'none') return t('subscription.crossSell.none')
    if (props.productKey === 'healthExpenses') {
      const addon = healthCrossSellAddons.value.find((a) => a.key === value)
      if (addon) return t(`bundles.${addon.labelKey}`)
    }
    return t(`subscription.crossSell.addons.${value}.name`)
  }
  return value
}

const answeredSteps = computed(() =>
  sequence.value
    .map((stepKey, idx) => ({ stepKey, idx, value: answers.value[stepKey] }))
    .filter((entry): entry is { stepKey: StepKey; idx: number; value: string } => Boolean(entry.value)),
)
</script>

<template>
  <section class="choices-recap" aria-labelledby="choices-recap-title">
    <h2 id="choices-recap-title" class="choices-recap__title">
      <button
        type="button"
        class="choices-recap__toggle"
        :aria-expanded="isOpen"
        aria-controls="choices-recap-body"
        @click="isOpen = !isOpen"
      >
        <span>{{ t('subscription.recap.title') }}</span>
        <svg
          class="choices-recap__chevron"
          :class="{ 'is-open': isOpen }"
          viewBox="0 0 20 20"
          width="16"
          height="16"
          aria-hidden="true"
        >
          <path d="M5 8l5 5 5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </h2>

    <div v-show="isOpen" id="choices-recap-body" class="choices-recap__body">
    <!-- Produit choisi -->
    <div class="choices-recap__entry">
      <span class="eyebrow">{{ t('subscription.recap.situation') }}</span>
      <div class="choices-recap__row">
        <span class="choices-recap__check" aria-hidden="true">
          <svg viewBox="0 0 20 20" width="14" height="14">
            <path d="M4 10l4 4 8-9" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
        <span class="choices-recap__label">{{ productLabel }}</span>
      </div>
      <router-link :to="homePath" class="choices-recap__change">
        {{ t('subscription.recap.change') }}
      </router-link>
    </div>

    <!-- Réponses du parcours -->
    <div
      v-for="entry in answeredSteps"
      :key="entry.stepKey"
      class="choices-recap__entry"
    >
      <span class="eyebrow">{{ stepLabel(entry.stepKey) }}</span>
      <div class="choices-recap__row">
        <span class="choices-recap__check" aria-hidden="true">
          <svg viewBox="0 0 20 20" width="14" height="14">
            <path d="M4 10l4 4 8-9" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </span>
        <span class="choices-recap__label">{{ answerLabel(entry.stepKey, entry.value) }}</span>
      </div>
      <router-link :to="urlBeforeStep(entry.idx)" class="choices-recap__change">
        {{ t('subscription.recap.change') }}
      </router-link>
    </div>
    </div>
  </section>
</template>

<style scoped>
.choices-recap {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  background: var(--color-white);
  border-radius: var(--radius-lg);
  padding: 1.25rem;
  border: 1px solid var(--border);
  box-shadow: var(--shadow-sm);
}

.choices-recap__title {
  margin: 0;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--text-muted);
}

/* Le titre devient un bouton plein largeur pour replier/déplier la carte. */
.choices-recap__toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
  margin: 0;
  padding: 0;
  background: none;
  border: 0;
  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
}
.choices-recap__toggle:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 4px;
  border-radius: var(--radius-xs);
}

.choices-recap__chevron {
  flex-shrink: 0;
  color: var(--text-muted);
  transition: transform var(--dur-fast) var(--ease-out);
}
.choices-recap__chevron.is-open {
  transform: rotate(180deg);
}

.choices-recap__body {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.choices-recap__entry {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  padding-top: 0.6rem;
  border-top: 1px solid color-mix(in oklab, var(--border) 60%, transparent);
}
.choices-recap__entry:first-of-type {
  padding-top: 0;
  border-top: 0;
}

/* Eyebrow utilisé pour le sous-titre : version atténuée dans la recap */
.choices-recap .eyebrow {
  font-size: 0.65rem;
  color: var(--text-muted);
  letter-spacing: 0.1em;
}
.choices-recap .eyebrow::before {
  width: 0.875rem;
  height: 1.5px;
  opacity: 0.5;
}

.choices-recap__row {
  display: flex;
  align-items: flex-start;
  gap: 0.45rem;
}

.choices-recap__check {
  display: grid;
  place-items: center;
  width: 0.95rem;
  height: 0.95rem;
  background: color-mix(in oklab, var(--primary) 75%, white);
  color: var(--color-white);
  border-radius: var(--radius-xs);
  flex-shrink: 0;
  margin-top: 0.15rem;
}
.choices-recap__check svg {
  width: 11px;
  height: 11px;
}

.choices-recap__label {
  font-size: var(--text-xs);
  font-weight: 500;
  color: var(--color-chocolate);
  line-height: 1.35;
}

.choices-recap__change {
  align-self: flex-start;
  font-size: 0.7rem;
  color: var(--text-muted);
  text-decoration: underline;
  text-underline-offset: 0.2em;
  font-weight: 500;
  transition: color var(--dur-fast) var(--ease-out);
}
.choices-recap__change:hover {
  color: var(--color-chocolate);
}
</style>
